import { Pause, Play } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { api } from '../../api';
import { t } from '../../i18n';
import type { ChildMemRecording, ChildTodayRecitation, PrayerTime } from '../../types';
import { Button } from '../shared/Button';
import { Modal } from '../shared/Modal';

const PRAYER_NAME_KEY: Record<PrayerTime, string> = {
  bomdod: 'prayer.bomdod',
  peshin: 'prayer.peshin',
  asr: 'prayer.asr',
  shom: 'prayer.shom',
  xufton: 'prayer.xufton',
};

const PRAYER_ORDER: PrayerTime[] = ['bomdod', 'peshin', 'asr', 'shom', 'xufton'];

function fmtDuration(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}

type RowItem = { uuid: string; audio_url: string; line1: string; line2: string };
type Group = { label: string; items: RowItem[] };

export function TodayRecordingsModal({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
  onChanged?: () => void;
}) {
  const [recitations, setRecitations] = useState<ChildTodayRecitation[] | null>(null);
  const [memos, setMemos] = useState<ChildMemRecording[] | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [activeUuid, setActiveUuid] = useState<string | null>(null);
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.myTodayRecitations().then((r) => setRecitations(r.recitations));
    api.myTodayMemorization().then((r) => setMemos(r.recordings));
    if (!audioRef.current) {
      const a = document.createElement('audio');
      a.preload = 'metadata';
      a.style.display = 'none';
      document.body.appendChild(a);
      audioRef.current = a;
      a.addEventListener('play', () => setPlaying(true));
      a.addEventListener('pause', () => setPlaying(false));
      a.addEventListener('ended', () => setPlaying(false));
    }
    return () => {
      audioRef.current?.pause();
      setActiveUuid(null);
      setPlaying(false);
    };
  }, [open]);

  const toggle = (uuid: string, url: string) => {
    const audio = audioRef.current;
    if (!audio) return;
    if (activeUuid === uuid) {
      if (audio.paused) void audio.play();
      else audio.pause();
      return;
    }
    setActiveUuid(uuid);
    audio.src = url;
    void audio.play().catch(() => setPlaying(false));
  };

  const loading = recitations === null || memos === null;

  const recGroups: Group[] = [];
  if (recitations) {
    for (const pt of PRAYER_ORDER) {
      const arr = recitations.filter((it) => it.prayer_time === pt);
      if (arr.length > 0) {
        recGroups.push({
          label: t(PRAYER_NAME_KEY[pt]),
          items: arr.map((it, idx) => ({
            uuid: it.uuid,
            audio_url: it.audio_url,
            line1: `${idx + 1}-marta · ${it.surah.number}. ${it.surah.name_uz}`,
            line2: fmtDuration(it.duration_seconds),
          })),
        });
      }
    }
  }

  const memGroups: Group[] = [];
  if (memos) {
    const slots = [...new Set(memos.map((m) => m.slot_key))].sort((a, b) => a.localeCompare(b));
    for (const slot of slots) {
      const arr = memos.filter((m) => m.slot_key === slot);
      memGroups.push({
        label: slot,
        items: arr.map((m, idx) => ({
          uuid: m.uuid,
          audio_url: m.audio_url,
          line1: `${m.rep_index ?? idx + 1}-marta · ${m.surah.number}. ${m.surah.name_uz} · ${m.ayah_from}–${m.ayah_to}-oyat`,
          line2: fmtDuration(m.duration_seconds),
        })),
      });
    }
  }

  const isEmpty = !loading && recGroups.length === 0 && memGroups.length === 0;
  const showModuleHeaders = recGroups.length > 0 && memGroups.length > 0;

  const renderGroups = (groups: Group[]) =>
    groups.map((g) => (
      <div key={g.label} className="flex flex-col gap-1.5">
        <div className="text-[11px] font-medium uppercase tracking-wide text-ink-subtle">{g.label}</div>
        <ul className="flex flex-col gap-1.5">
          {g.items.map((it) => {
            const isPlaying = activeUuid === it.uuid && playing;
            return (
              <li
                key={it.uuid}
                className="flex items-center gap-3 rounded-md border-hairline border-border bg-surface px-3 py-2"
              >
                <button
                  onClick={() => toggle(it.uuid, it.audio_url)}
                  aria-label={isPlaying ? 'pause' : 'play'}
                  className="flex h-8 w-8 items-center justify-center rounded-full bg-surface-muted text-ink active:scale-[0.97]"
                >
                  {isPlaying ? <Pause size={14} /> : <Play size={14} className="ml-0.5" />}
                </button>
                <div className="flex flex-1 flex-col text-[13px]">
                  <span>{it.line1}</span>
                  <span className="text-[11px] text-ink-muted tabular-nums">{it.line2}</span>
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    ));

  return (
    <Modal open={open} onClose={onClose} title={t('child.records.title')}>
      <div className="flex flex-col gap-3">
        {loading ? (
          <p className="text-[13px] text-ink-muted">{t('common.loading')}</p>
        ) : isEmpty ? (
          <p className="text-[13px] text-ink-muted">{t('child.records.empty')}</p>
        ) : (
          <div className="flex max-h-[60vh] flex-col gap-4 overflow-y-auto">
            {recGroups.length > 0 ? (
              <div className="flex flex-col gap-3">
                {showModuleHeaders ? (
                  <div className="text-[11px] font-medium uppercase tracking-wide text-ink-subtle/70">
                    {t('module.recitation')}
                  </div>
                ) : null}
                {renderGroups(recGroups)}
              </div>
            ) : null}
            {memGroups.length > 0 ? (
              <div className="flex flex-col gap-3">
                {showModuleHeaders ? (
                  <div className="text-[11px] font-medium uppercase tracking-wide text-ink-subtle/70">
                    {t('module.memorization')}
                  </div>
                ) : null}
                {renderGroups(memGroups)}
              </div>
            ) : null}
          </div>
        )}
        <Button variant="ghost" onClick={onClose} fullWidth>
          {t('common.back')}
        </Button>
      </div>
    </Modal>
  );
}
